import React from "react";
import {
    TextB, TextItalic, TextStrikethrough, ListBullets, ListNumbers,
    Quotes, Link as LinkIcon, Code, TextHOne, TextHTwo, TextHThree,
} from "@phosphor-icons/react";

/**
 * Markdown toolbar that operates on a textarea ref.
 * - Inline marks (bold, italic, strike, code, link) wrap the current selection.
 * - Block formats (h1/h2/h3, quote, bullet list, numbered list) toggle a prefix
 *   on each selected line.
 * After insertion the textarea selection is restored around the new content so
 * typing continues seamlessly.
 */
export default function MarkdownToolbar({ textareaRef, value, onChange }) {
    const apply = (fn) => {
        const ta = textareaRef.current;
        if (!ta) return;
        const start = ta.selectionStart ?? value.length;
        const end = ta.selectionEnd ?? value.length;
        const selected = value.slice(start, end);
        const { text, selStart, selEnd } = fn({
            before: value.slice(0, start),
            after: value.slice(end),
            selected,
            start,
            end,
        });
        onChange(text);
        requestAnimationFrame(() => {
            ta.focus();
            ta.setSelectionRange(selStart, selEnd);
        });
    };

    const wrap = (mark) => apply(({ before, after, selected, start }) => {
        const inner = selected || "text";
        const text = `${before}${mark}${inner}${mark}${after}`;
        const selStart = start + mark.length;
        return { text, selStart, selEnd: selStart + inner.length };
    });

    const blockPrefix = (prefix) => apply(({ before, after, selected, start }) => {
        // Apply prefix to each line in the selection. If selection is empty,
        // prefix the current line.
        const lines = (selected || "list item").split("\n");
        let counter = 0;
        const transformed = lines.map((line) => {
            if (prefix === "1. ") {
                counter += 1;
                return `${counter}. ${line.replace(/^(\d+\.\s|[-*]\s|>\s|#+\s)/, "")}`;
            }
            return `${prefix}${line.replace(/^(\d+\.\s|[-*]\s|>\s|#+\s)/, "")}`;
        }).join("\n");
        const text = `${before}${transformed}${after}`;
        return { text, selStart: start, selEnd: start + transformed.length };
    });

    const insertLink = () => apply(({ before, after, selected, start }) => {
        const url = window.prompt("Link URL:", "https://");
        if (!url) return { text: `${before}${selected}${after}`, selStart: start, selEnd: start + selected.length };
        const label = selected || "link text";
        const token = `[${label}](${url})`;
        const text = `${before}${token}${after}`;
        return { text, selStart: start + 1, selEnd: start + 1 + label.length };
    });

    return (
        <div className="flex flex-wrap items-center gap-1 mb-2 p-2 border border-zinc-800 bg-[#0A0A0A]" data-testid="md-toolbar">
            <ToolbarButton testid="md-bold" title="Bold (Cmd/Ctrl+B)" onClick={() => wrap("**")}><TextB size={16} weight="bold" /></ToolbarButton>
            <ToolbarButton testid="md-italic" title="Italic (Cmd/Ctrl+I)" onClick={() => wrap("*")}><TextItalic size={16} weight="bold" /></ToolbarButton>
            <ToolbarButton testid="md-strike" title="Strikethrough" onClick={() => wrap("~~")}><TextStrikethrough size={16} weight="bold" /></ToolbarButton>
            <ToolbarButton testid="md-code" title="Inline code" onClick={() => wrap("`")}><Code size={16} weight="bold" /></ToolbarButton>
            <ToolbarSeparator />
            <ToolbarButton testid="md-h1" title="Heading 1" onClick={() => blockPrefix("# ")}><TextHOne size={16} weight="bold" /></ToolbarButton>
            <ToolbarButton testid="md-h2" title="Heading 2" onClick={() => blockPrefix("## ")}><TextHTwo size={16} weight="bold" /></ToolbarButton>
            <ToolbarButton testid="md-h3" title="Heading 3" onClick={() => blockPrefix("### ")}><TextHThree size={16} weight="bold" /></ToolbarButton>
            <ToolbarSeparator />
            <ToolbarButton testid="md-ul" title="Bullet list" onClick={() => blockPrefix("- ")}><ListBullets size={16} weight="bold" /></ToolbarButton>
            <ToolbarButton testid="md-ol" title="Numbered list" onClick={() => blockPrefix("1. ")}><ListNumbers size={16} weight="bold" /></ToolbarButton>
            <ToolbarButton testid="md-quote" title="Blockquote" onClick={() => blockPrefix("> ")}><Quotes size={16} weight="bold" /></ToolbarButton>
            <ToolbarSeparator />
            <ToolbarButton testid="md-link" title="Insert link" onClick={insertLink}><LinkIcon size={16} weight="bold" /></ToolbarButton>
        </div>
    );
}

function ToolbarButton({ onClick, title, children, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            title={title}
            data-testid={testid}
            className="w-9 h-9 grid place-items-center border border-zinc-800 hover:border-[#FFD000] hover:text-[#FFD000] text-zinc-300 transition-colors"
        >
            {children}
        </button>
    );
}

function ToolbarSeparator() {
    return <span className="w-px h-5 bg-zinc-800 mx-1" />;
}
